/**
 * Self-repair for npm's install-script policy (npm 11.17 warn / npm 12 block).
 *
 * npm now blocks dependency install scripts by default. Ours that matter:
 * keytar's `prebuild-install` (fetches its native binary). When blocked,
 * keytar has no .node file — and @azure/msal-node-extensions imports keytar
 * STATICALLY on every platform (even Windows, where DPAPI never uses it), so
 * the entire secure-storage layer fails to load. Field signature (observed
 * live 2026-08-12, Windows + npm 11.17): device-code sign-in "works"
 * (in-memory), then every fresh auth provider finds an empty cache —
 * "not authorized" warnings and "Silent token acquisition failed" at
 * environment discovery.
 *
 * The repair leans on an npm 12 asymmetry we verified empirically:
 * - The INSTALLING project's allowlist decides at install time — a published
 *   package cannot self-authorize its dependencies' scripts (by design).
 * - But `npm rebuild` run INSIDE the installed package directory treats OUR
 *   package.json as the project — and we ship `allowScripts` entries for
 *   keytar and msal-node-extensions there, so the rebuild is pre-authorized
 *   and fetches the missing binary. No user npm-config changes needed.
 */

import { execFile } from "node:child_process";
import { createRequire } from "node:module";
import { dirname, join } from "node:path";
import { existsSync, readFileSync } from "node:fs";
import { fileURLToPath } from "node:url";
import { logger } from "../utils/logger.js";

const REBUILD_TARGETS = ["keytar", "@azure/msal-node-extensions"] as const;

/** The exact command a user can run when the automatic repair can't. */
export const MANUAL_REPAIR_COMMAND =
  "npm install -g --allow-scripts=@azure/msal-node-extensions,keytar powerautomate-mcp";

/**
 * Can the native secure-storage stack load? keytar is the canary: it is the
 * one dependency whose install script fetches a native binary, and
 * msal-node-extensions cannot be imported without it.
 */
export function nativeStorageLoadable(): boolean {
  return nativeStorageLoadError() === null;
}

/** Why the native stack can't load — null when it loads fine. */
export function nativeStorageLoadError(): string | null {
  try {
    const require = createRequire(import.meta.url);
    require("keytar");
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
}

/**
 * A load failure naming libsecret is a missing SYSTEM library (Linux), not a
 * blocked install script — `npm rebuild` cannot fix it, and telling the user
 * to reinstall the package would send them in a circle.
 */
export function isSystemDependencyFailure(loadError: string | null): boolean {
  return loadError !== null && /libsecret/i.test(loadError);
}

const LIBSECRET_INSTRUCTION =
  "Install the system library secure storage needs, then retry: " +
  "sudo apt-get install libsecret-1-0 gnome-keyring (Debian/Ubuntu) or " +
  "sudo dnf install libsecret gnome-keyring (Fedora/RHEL)";

/** The right manual fix for the CURRENT failure — npm-policy vs system library. */
export function manualRepairInstruction(loadError?: string | null): string {
  const err = loadError === undefined ? nativeStorageLoadError() : loadError;
  return isSystemDependencyFailure(err)
    ? LIBSECRET_INSTRUCTION
    : `Reinstall with install scripts allowed: ${MANUAL_REPAIR_COMMAND}`;
}

/** Walk up from the running bundle to our own package root. */
export function findOwnPackageRoot(startDir?: string): string | null {
  let dir = startDir ?? dirname(fileURLToPath(import.meta.url));
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, "package.json");
    if (existsSync(candidate)) {
      try {
        const parsed = JSON.parse(readFileSync(candidate, "utf-8")) as { name?: string };
        if (parsed.name === "powerautomate-mcp") return dir;
      } catch {
        // keep walking
      }
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

export type NativeRepairResult = "not-needed" | "repaired" | "failed";

/**
 * Attempt the in-place rebuild. Fixed arguments, no shell interpolation of
 * anything user-controlled; `shell: true` on Windows only because npm is
 * npm.cmd there and CreateProcess won't exec .cmd files directly.
 */
export async function repairNativeStorage(
  deps: {
    loadable?: () => boolean;
    loadError?: () => string | null;
    packageRoot?: string | null;
    run?: (cwd: string) => Promise<void>;
  } = {}
): Promise<NativeRepairResult> {
  const loadable = deps.loadable ?? nativeStorageLoadable;
  if (loadable()) return "not-needed";

  // A missing system library (libsecret on Linux) is not an npm-script
  // problem — a rebuild would spend up to 3 minutes changing nothing.
  const loadError = (deps.loadError ?? nativeStorageLoadError)();
  if (isSystemDependencyFailure(loadError)) {
    logger.warn({ loadError }, "Native-storage repair: system library missing, rebuild would not help");
    return "failed";
  }

  const root = deps.packageRoot !== undefined ? deps.packageRoot : findOwnPackageRoot();
  if (!root) {
    logger.warn("Native-storage repair: could not locate our own package root");
    return "failed";
  }

  const run =
    deps.run ??
    ((cwd: string) =>
      new Promise<void>((resolve, reject) => {
        execFile(
          process.platform === "win32" ? "npm.cmd" : "npm",
          ["rebuild", ...REBUILD_TARGETS],
          { cwd, timeout: 180_000, shell: process.platform === "win32" },
          (error) => (error ? reject(error) : resolve())
        );
      }));

  try {
    logger.info({ root }, "Repairing native secure-storage modules (npm rebuild)");
    await run(root);
  } catch (err) {
    logger.warn(
      { err: err instanceof Error ? err.message : String(err) },
      "Native-storage repair: npm rebuild failed"
    );
    return "failed";
  }

  return loadable() ? "repaired" : "failed";
}
